import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function Gallery() {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobileAnimation, setIsMobileAnimation] = useState(false);

  const getGalleryImageSrc = (imagePath) => {
    if (!imagePath) return '/img/Gallery/opd1.jpg';
    return imagePath.startsWith('/uploads/') ? imagePath : `/uploads/${imagePath}`;
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchImages();
  }, [selectedCategory, currentPage]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateAnimationMode = (event) => {
      setIsMobileAnimation(event.matches);
    };

    setIsMobileAnimation(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateAnimationMode);
      return () => mediaQuery.removeEventListener('change', updateAnimationMode);
    }

    mediaQuery.addListener(updateAnimationMode);
    return () => mediaQuery.removeListener(updateAnimationMode);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/gallery/categories/list');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchImages = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20
      };
      if (selectedCategory) params.category = selectedCategory;

      const response = await axios.get('/api/gallery', { params });
      setImages(response.data.images);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = 'auto';
  };

  const navigateLightbox = (direction) => {
    const currentIndex = images.findIndex(img => img.id === lightboxImage.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % images.length;
    } else {
      newIndex = (currentIndex - 1 + images.length) % images.length;
    }
    
    setLightboxImage(images[newIndex]);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (lightboxImage) {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowRight') {
          navigateLightbox('next');
        } else if (e.key === 'ArrowLeft') {
          navigateLightbox('prev');
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxImage, images]);

  if (loading && images.length === 0) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(0, 61, 130, 0.9), rgba(0, 61, 130, 0.9)), url(/img/Gallery/opd1.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h2 className="text-white mb-3" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif", textTransform: 'uppercase', letterSpacing: '1px' }}>
              TGH Gallery
            </h2>
            <p className="text-white mt-3 mb-0" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              Explore our facilities, events, and community outreach
            </p>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  Gallery
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
        <div className="container">
          {/* Category Filter */}
          <div className="row mb-4">
            <div className="col-md-4">
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {images.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-0">No images found in this category.</p>
            </div>
          ) : (
            <div className="row g-3">
              {images.map((img, index) => (
                <div
                  key={img.id}
                  className="col-xl-4 col-lg-6 col-md-6 col-12"
                  style={{
                    animation: `${isMobileAnimation ? 'galleryFadeUpMobile' : 'galleryFadeUp'} ${isMobileAnimation ? '0.35s' : '0.5s'} ease-out ${Math.min(index * (isMobileAnimation ? 0.035 : 0.06), isMobileAnimation ? 0.2 : 0.36)}s both`
                  }}
                >
                  <div
                    className="card h-100 shadow-sm border-0"
                    role="button"
                    onClick={() => openLightbox(img)}
                    style={{
                      transition: isMobileAnimation
                        ? 'transform 0.18s ease, box-shadow 0.18s ease'
                        : 'transform 0.25s ease, box-shadow 0.25s ease',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = isMobileAnimation ? 'translateY(-2px)' : 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = isMobileAnimation
                        ? '0 8px 16px rgba(0,0,0,0.12)'
                        : '0 14px 30px rgba(0,0,0,0.16)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <div className="tgh-card-media">
                      <img
                        src={getGalleryImageSrc(img.image)}
                        alt={img.caption || 'Gallery image'}
                        className="card-img-top"
                        style={{
                          transition: isMobileAnimation ? 'transform 0.22s ease' : 'transform 0.35s ease'
                        }}
                        onError={(e) => {
                          e.target.src = '/img/Gallery/opd1.jpg';
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = isMobileAnimation ? 'scale(1.015)' : 'scale(1.04)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />
                    </div>
                    <div className="card-body">
                      <span className="badge bg-primary mb-2">{img.category || 'General'}</span>
                      <p className="card-text mb-0" style={{ fontSize: '0.9rem', minHeight: '40px' }}>
                        {img.caption || <em className="text-muted">No caption</em>}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="row mt-5">
              <div className="col-12">
                <nav aria-label="Gallery pagination">
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </section>

      <style>{`
        @keyframes galleryFadeUp {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes galleryFadeUpMobile {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .card {
            animation: none !important;
            transition: none !important;
          }
          .card img {
            transition: none !important;
          }
        }
      `}</style>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
        >
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '2rem',
              right: '2rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#fff',
              fontSize: '2rem',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            ×
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
                style={{
                  position: 'absolute',
                  left: '2rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: '#fff',
                  fontSize: '2rem',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ‹
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
                style={{
                  position: 'absolute',
                  right: '2rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: '#fff',
                  fontSize: '2rem',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ›
              </button>
            </>
          )}

          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <img
              src={getGalleryImageSrc(lightboxImage.image)}
              alt={lightboxImage.caption || 'Gallery image'}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              onError={(e) => {
                e.target.src = '/img/Gallery/opd1.jpg';
              }}
            />
            {lightboxImage.caption && (
              <p
                style={{
                  color: '#fff',
                  marginTop: '1rem',
                  fontSize: '1.1rem',
                  textAlign: 'center',
                  maxWidth: '800px'
                }}
              >
                {lightboxImage.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Gallery;
